"use client";

import { useFeatureFlagVariantKey } from "posthog-js/react";
import { MY_DETAILS_TEST_FLAG, useSignupFlowStore } from "@./state";
import {
  isFlagDisabledForMultiCat,
} from "libs/state/src/lib/stores/flows/fresh/signupFlow/featureFlags";
import { parseAbTestVariant, type AbTestVariant } from "@utils/types";

/**
 * Prefers a stored assignment so an `identify()` re-roll can't flip the experience mid-flow,
 * falling back to PostHog for unassigned users.
 *
 * A stored value that isn't a valid variant counts as unassigned, so the caller re-rolls rather
 * than carrying on with nothing. PostHog is only read when there is nothing stored, so an
 * already-assigned user doesn't log a second `$feature_flag_called`.
 */
export const pickMyDetailsTestVariant = (
  storedVariant: unknown,
  readPosthogVariant: () => unknown,
  activeCatCount: number,
): AbTestVariant | undefined => {
  if (isFlagDisabledForMultiCat(MY_DETAILS_TEST_FLAG, activeCatCount)) {
    return undefined;
  }

  return parseAbTestVariant(storedVariant) ?? parseAbTestVariant(readPosthogVariant());
};

/**
 * Resolves the my-details variant for rendering. Shares its precedence rules with the sign-up
 * submit path via `pickMyDetailsTestVariant`, so both agree on which variant a user is in.
 */
export const useMyDetailsTestVariant = (): AbTestVariant | undefined => {
  const storedVariant = useSignupFlowStore((state) => state.featureFlags?.[MY_DETAILS_TEST_FLAG]);
  const activeCatCount = useSignupFlowStore((state) => state.getActiveCatIds().length);
  const posthogVariant = useFeatureFlagVariantKey(MY_DETAILS_TEST_FLAG);

  return pickMyDetailsTestVariant(storedVariant, () => posthogVariant, activeCatCount);
};

export const useIsMyDetailsTestVariant = (): boolean =>
  useMyDetailsTestVariant() === "test";
